import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import {
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  collection,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";

export default function ItemDetail({ user }) {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [editedText, setEditedText] = useState("");
  const [editedPseudonym, setEditedPseudonym] = useState("");
  const [newImageFile, setNewImageFile] = useState(null);
  const [editedCategory, setEditedCategory] = useState("");
  const [editedBrand, setEditedBrand] = useState("");
  const [editedColor, setEditedColor] = useState("");
  const [editedMaterial, setEditedMaterial] = useState("");
  const [editedTags, setEditedTags] = useState("");
  const [styledLooks, setStyledLooks] = useState([]);

  useEffect(() => {
    const fetchItem = async () => {
      const itemRef = doc(db, "users", user.uid, "wardrobe", itemId);
      const itemSnap = await getDoc(itemRef);
      if (itemSnap.exists()) {
        const data = itemSnap.data();
        setItem({ id: itemSnap.id, ...data });
        setEditedName(data.name || "");
        setEditedText(data.inspiration || "");
        setEditedPseudonym(data.pseudonym || "");
        setEditedCategory(data.category || "");
        setEditedBrand(data.brand || "");
        setEditedColor(data.color || "");
        setEditedMaterial(data.material || "");
        setEditedTags(Array.isArray(data.tags) ? data.tags.join(", ") : data.tags || "");
      }
    };
    fetchItem();
  }, [user.uid, itemId]);

  useEffect(() => {
    const looksQuery = query(
      collection(db, "stylingEntries"),
      where("itemId", "==", itemId),
      where("userId", "==", user.uid)
    );
    return onSnapshot(looksQuery, (snapshot) => {
      setStyledLooks(
        snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() }))
      );
    });
  }, [itemId, user.uid]);

  const deleteItem = async () => {
    const confirm = window.confirm("Are you sure you want to delete this item?");
    if (!confirm) return;
    try {
      if (item.imageUrl) {
        try {
          await deleteObject(ref(getStorage(), item.imageUrl));
        } catch (storageError) {
          if (storageError.code !== "storage/object-not-found") throw storageError;
        }
      }
      const itemRef = doc(db, "users", user.uid, "wardrobe", itemId);
      await deleteDoc(itemRef);
      navigate("/wardrobe");
    } catch (error) {
      console.error("Unable to delete wardrobe piece", error);
      alert("We couldn't delete this piece. Please try again.");
    }
  };

  const saveEdits = async () => {
    let imageUrl = item.imageUrl;
    if (newImageFile) {
      const storage = getStorage();
      const imageRef = ref(storage, `wardrobeImages/${user.uid}/${Date.now()}_${newImageFile.name}`);
      await uploadBytes(imageRef, newImageFile);
      imageUrl = await getDownloadURL(imageRef);
    }

    const profileSnapshot = await getDoc(doc(db, "users", user.uid));
    const profileName = profileSnapshot.exists() ? profileSnapshot.data().name : "";
    const contributorName =
      editedPseudonym.trim() || profileName || user.displayName || "Style lover";
    const normalizedTags = editedTags
      .split(",")
      .map((tag) => tag.trim().toLowerCase())
      .filter(Boolean);

    const itemRef = doc(db, "users", user.uid, "wardrobe", itemId);
    await updateDoc(itemRef, {
      name: editedName,
      inspiration: editedText,
      imageUrl,
      pseudonym: editedPseudonym,
      contributorName,
      category: editedCategory.trim(),
      brand: editedBrand.trim(),
      color: editedColor.trim(),
      material: editedMaterial.trim(),
      tags: normalizedTags,
      isFounderPiece:
        item.isFounderPiece || user.email === "corinanicoara01@gmail.com",
    });
    setItem(prev => ({
      ...prev,
      name: editedName,
      inspiration: editedText,
      imageUrl,
      pseudonym: editedPseudonym,
      contributorName,
      category: editedCategory.trim(),
      brand: editedBrand.trim(),
      color: editedColor.trim(),
      material: editedMaterial.trim(),
      tags: normalizedTags,
      isFounderPiece:
        prev.isFounderPiece || user.email === "corinanicoara01@gmail.com",
    }));
    setEditing(false);
    setNewImageFile(null);
  };

  const toggleShare = async () => {
    const profileSnapshot = await getDoc(doc(db, "users", user.uid));
    const profileName = profileSnapshot.exists() ? profileSnapshot.data().name : "";
    const contributorName =
      item.pseudonym || profileName || user.displayName || "Style lover";
    const itemRef = doc(db, "users", user.uid, "wardrobe", itemId);
    await updateDoc(itemRef, { isPublic: !item.isPublic, contributorName });
    setItem(prev => ({ ...prev, isPublic: !prev.isPublic, contributorName }));
  };

  if (!item) return <div className="p-10">Loading...</div>;

  return (
    <div
      className="min-h-screen relative"
      style={{
        backgroundImage: "url('/fabric-588884_1280.jpg')",
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-rose-100 bg-opacity-60 backdrop-blur-sm"></div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 py-16">
        <div className="flex justify-between items-start mb-6">
          <div className="w-full">
            {editing ? (
              <div className="space-y-4">
                <input
                  type="text"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  className="w-full border border-neutral-300 p-2 rounded text-sm"
                  placeholder="Item name"
                />
                <textarea
                  value={editedText}
                  onChange={(e) => setEditedText(e.target.value)}
                  className="w-full border border-neutral-300 p-2 rounded text-sm"
                  placeholder="Reflection or inspiration"
                />
                <input
                  type="text"
                  value={editedPseudonym}
                  onChange={(e) => setEditedPseudonym(e.target.value)}
                  className="w-full border border-neutral-300 p-2 rounded text-sm"
                  placeholder="Name or pseudonym (optional)"
                />
                <input
                  type="text"
                  value={editedCategory}
                  onChange={(e) => setEditedCategory(e.target.value)}
                  className="w-full border border-neutral-300 p-2 rounded text-sm"
                  placeholder="Category (e.g. Dress, Jacket, Skirt)"
                />
                <details className="border border-neutral-300 bg-white p-4">
                  <summary className="cursor-pointer text-sm font-medium text-neutral-700">
                    Optional matching details
                  </summary>
                  <div className="mt-4 space-y-3">
                    <input
                      type="text"
                      value={editedBrand}
                      onChange={(e) => setEditedBrand(e.target.value)}
                      className="w-full border border-neutral-300 p-2 rounded text-sm"
                      placeholder="Brand"
                    />
                    <input
                      type="text"
                      value={editedColor}
                      onChange={(e) => setEditedColor(e.target.value)}
                      className="w-full border border-neutral-300 p-2 rounded text-sm"
                      placeholder="Main color or pattern"
                    />
                    <input
                      type="text"
                      value={editedMaterial}
                      onChange={(e) => setEditedMaterial(e.target.value)}
                      className="w-full border border-neutral-300 p-2 rounded text-sm"
                      placeholder="Material"
                    />
                    <input
                      type="text"
                      value={editedTags}
                      onChange={(e) => setEditedTags(e.target.value)}
                      className="w-full border border-neutral-300 p-2 rounded text-sm"
                      placeholder="Style words, separated by commas"
                    />
                  </div>
                </details>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setNewImageFile(e.target.files[0])}
                  className="w-full text-sm"
                />
                <button
                  onClick={saveEdits}
                  className="mt-2 bg-rose-600 text-white px-4 py-2 rounded hover:bg-rose-700 transition"
                >
                  Save Changes
                </button>
              </div>
            ) : (
              <>
                <h1 className="text-3xl font-semibold text-rose-800 mb-2">{item.name}</h1>
                <p className="italic text-sm text-neutral-700 mb-2">{item.inspiration}</p>
                {(item.brand || item.color || item.material) && (
                  <p className="mb-2 text-sm text-neutral-600">
                    {[item.brand, item.color, item.material].filter(Boolean).join(" · ")}
                  </p>
                )}
                {item.tags?.length > 0 && (
                  <div className="mb-3 flex flex-wrap gap-2">
                    {item.tags.map((tag) => (
                      <span key={tag} className="bg-rose-100 px-2 py-1 text-xs text-rose-800">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-xs text-neutral-500 italic mb-4">
                  {item.pseudonym ? `Shared as ${item.pseudonym}` : "Private name"}
                </p>
                <button
                  onClick={() => setEditing(true)}
                  className="text-xs text-neutral-500 underline"
                >
                  Edit
                </button>
              </>
            )}
          </div>

          <button
            onClick={deleteItem}
            className="text-xs text-neutral-400 underline hover:text-red-500"
          >
            Delete
          </button>
        </div>

        <div className="flex justify-center mb-10">
          <div className="relative transform rotate-1 shadow-xl border border-neutral-200 bg-gradient-to-br from-white via-neutral-100 to-neutral-200 p-3 rounded-xl w-72">
            <img
              src={item.imageUrl}
              alt={item.name}
              className="w-full object-cover aspect-[3/4] border border-neutral-300"
            />
          </div>
        </div>

        <div className="text-center">
          <button
            onClick={toggleShare}
            className="text-sm text-rose-600 underline hover:text-rose-800"
          >
            {item.isPublic ? "Unshare from Community" : "Share for Inspiration"}
          </button>
          <div className="mx-auto mt-8 grid max-w-2xl gap-4 sm:grid-cols-2">
            <Link
              to={`/item/${item.id}/style`}
              className="border border-rose-300 bg-white p-5 text-left no-underline hover:border-rose-600"
            >
              <p className="text-xs uppercase tracking-wider text-rose-700">I have styling ideas</p>
              <h2 className="mt-2 text-xl text-stone-900">Share how I styled it</h2>
              <p className="mt-2 text-sm leading-6 text-stone-600">
                Upload a complete or cropped outfit and explain what worked.
              </p>
            </Link>
            <Link
              to={`/style-requests/new?piece=${item.id}`}
              className="bg-stone-900 p-5 text-left text-white no-underline hover:bg-rose-800"
            >
              <p className="text-xs uppercase tracking-wider text-rose-200">I want fresh ideas</p>
              <h2 className="mt-2 text-xl">Help me style it</h2>
              <p className="mt-2 text-sm leading-6 text-stone-200">
                Ask the community for suggestions centered on this piece.
              </p>
            </Link>
          </div>
        </div>

        <section className="mt-14 border-t border-stone-300 pt-10">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-rose-700">Your styling portfolio</p>
              <h2 className="mt-2 text-3xl text-stone-900">Ways you styled this piece</h2>
            </div>
            <span className="text-sm text-stone-500">{styledLooks.length} looks</span>
          </div>
          {styledLooks.length ? (
            <div className="mt-7 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {styledLooks.map((look) => (
                <article key={look.id} className="border border-stone-300 bg-white p-4 text-left">
                  <img src={look.photoURL} alt={`Styled ${item.name}`} className="aspect-[4/5] w-full object-cover" />
                  {look.caption && <p className="mt-4 text-sm leading-6 text-stone-700">{look.caption}</p>}
                  {(look.occasion || look.mood) && (
                    <p className="mt-3 text-xs uppercase tracking-wider text-rose-700">
                      {[look.occasion, look.mood].filter(Boolean).join(" · ")}
                    </p>
                  )}
                </article>
              ))}
            </div>
          ) : (
            <p className="mt-5 text-sm text-stone-500">
              No styled looks yet. Share the first way you made this piece your own.
            </p>
          )}
        </section>
      </div>
    </div>
  );
}
